import { compact, isArray } from 'lodash-es';
import { useMemo } from 'react';
import { useSecuritiesContext } from '../../contexts';
import { HStack, Stack } from '../Core';
import { CurrencyIcon } from '../CurrencyIcon';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import { SecurityRenderer } from './SecurityRenderer';

interface SecuritiesRendererProps {
  symbols: string[] | undefined;
  showIcon?: boolean;
  iconSize?: number;
}

export const SecuritiesRenderer = ({ symbols, showIcon = true, iconSize = 16 }: SecuritiesRendererProps) => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const securities = useMemo(
    () => compact(symbols?.map(s => securitiesBySymbol.get(s))),
    [securitiesBySymbol, symbols]
  );

  if (!symbols || symbols.length === 0) {
    return <></>;
  }

  const firstCurrency = securities.at(0)?.BaseCurrency;

  return (
    <HStack verticalAlign="middle" gap="spacing4" color="colorTextImportant">
      {showIcon && firstCurrency && <CurrencyIcon size={iconSize} currency={firstCurrency} stacked />}

      <Tooltip showUnderline tooltip={<SecuritiesTooltipContent symbols={symbols} />}>
        <Text whiteSpace="nowrap">Multi ({symbols.length})</Text>
      </Tooltip>
    </HStack>
  );
};

const SecuritiesTooltipContent = ({ symbols }: { symbols: string[] }) => {
  const { securitiesBySymbol } = useSecuritiesContext();

  return (
    <Stack gap="spacing4">
      {symbols.map(symbol => (
        <Text key={symbol}>{securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol}</Text>
      ))}
    </Stack>
  );
};

interface SecurityOrSecuritiesRendererProps {
  symbolOrSymbols: string | string[] | undefined;
  showIcon?: boolean;
  iconSize?: number;
}

/** If you have _either_ a Security, or an array of Security, you can use this component */
export const SecurityOrSecuritiesRenderer = ({ symbolOrSymbols, ...props }: SecurityOrSecuritiesRendererProps) => {
  if (!symbolOrSymbols) {
    return <></>;
  }

  if (isArray(symbolOrSymbols)) {
    return <SecuritiesRenderer symbols={symbolOrSymbols} {...props} />;
  }

  return <SecurityRenderer symbol={symbolOrSymbols} {...props} />;
};
